import { useEffect, useRef } from 'react';
import { useWebviewBridge, useWebviewEvents } from '@contexts/webview-bridge';

import type { UseAndroidSwipeRefreshOptions } from './use-android-swipe-refresh.type';

const MAIN_SCROLL_ATTR = 'data-main-scroll';
// Headless UI's Dialog sets the role. The Drawer is a native <dialog>, which has the role without the attribute; one
// that stays mounted while closed keeps `open` and is hidden instead.
const OPEN_DIALOG_SELECTOR = '[role="dialog"], dialog[open]:not([hidden])';
// dnd-kit marks every drag handle, and keeps the mark on a disabled one. The native refresh reacts to a downward move
// before the web sees it, so a drag that starts on a handle at the top of the page would reload it instead of dragging.
// The long-press rule in src/index.css matches the same handles: keep the two selectors in step.
const DRAG_HANDLE_SELECTOR =
  '[aria-roledescription="sortable"]:not([aria-disabled="true"]), [aria-roledescription="draggable"]:not([aria-disabled="true"])';

/**
 * Communicates whether Android's native pull-to-refresh should be active.
 *
 * Only active inside the Android webview (detected via `globalThis.Android`).
 * On browser and iOS the hook is a no-op: no listeners are attached.
 *
 * PTR is enabled only when the user is interacting with the main page content
 * at the top — not on a drag handle, while a modal or drawer is open, or in any
 * other scrollable container (tables, lists, sidebars, etc.).
 *
 * Decision is made at touch start (so native knows before the gesture moves)
 * and refreshed on scroll inside the main container.
 */
export const useAndroidSwipeRefresh = ({ initialEnabled = true }: UseAndroidSwipeRefreshOptions = {}) => {
  const lastValueRef = useRef<boolean | null>(null);
  const { sendPullToRefreshEnabled } = useWebviewEvents();
  const { isInitialized } = useWebviewBridge();

  useEffect(() => {
    if ((globalThis as { Android?: unknown }).Android === undefined) return;
    // Wait until the bridge is ready. Otherwise `send` is the pre-init stub
    // that throws, the initial ping is swallowed, and
    // `lastValueRef` has already been flipped, so the re-run after init
    // early-returns and native never hears from us.
    if (!isInitialized) return;

    const update = (enabled: boolean) => {
      if (lastValueRef.current === enabled) return;
      lastValueRef.current = enabled;
      sendPullToRefreshEnabled(enabled).catch((): void => undefined);
    };

    const isScrollable = (el: HTMLElement) => {
      const overflowY = getComputedStyle(el).overflowY;
      return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
    };

    const resolveEnabledForTarget = (target: Element): boolean => {
      // An open dialog covers the whole page, its backdrop included, and the backdrop sits outside the dialog element.
      if (document.querySelector(OPEN_DIALOG_SELECTOR)) return false;

      let el: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement;
      while (el) {
        if (isScrollable(el)) {
          if (!el.hasAttribute(MAIN_SCROLL_ATTR)) return false;
          return el.scrollTop === 0;
        }
        el = el.parentElement;
      }
      return false;
    };

    let rafId: number | null = null;
    // While a finger is on a drag handle, nothing re-enables PTR: not a second finger, and not the scroll that
    // dnd-kit's autoscroll causes when the drag nears the top of the page.
    let isHandleTouchActive = false;

    const handleTouchStart = (event: TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // A lone finger means the previous gesture is over even if its touchend was lost, which happens when the touched
      // node leaves the DOM mid-gesture.
      if (isHandleTouchActive && event.touches.length > 1) return;
      isHandleTouchActive = target.closest(DRAG_HANDLE_SELECTOR) !== null;
      update(!isHandleTouchActive && resolveEnabledForTarget(target));
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length === 0) isHandleTouchActive = false;
    };

    const handleScroll = (event: Event) => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        const target = event.target;

        if (!(target instanceof HTMLElement) || isHandleTouchActive) return;
        // While any dialog is open, do not emit enable=true from residual
        // scroll events (momentum / programmatic) on the main container —
        // the modal is on top and PTR must stay disabled.
        if (document.querySelector(OPEN_DIALOG_SELECTOR)) return;
        if (!target.hasAttribute(MAIN_SCROLL_ATTR)) return;

        update(target.scrollTop === 0);
      });
    };

    document.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true });
    document.addEventListener('touchend', handleTouchEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { capture: true, passive: true });
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });

    update(initialEnabled);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      document.removeEventListener('touchend', handleTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', handleTouchEnd, { capture: true });
      document.removeEventListener('scroll', handleScroll, { capture: true });
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [sendPullToRefreshEnabled, isInitialized, initialEnabled]);
};
